import { useEffect, useMemo, useState } from "react";
import { Download, FileText, Printer, RefreshCw } from "lucide-react";
import { toast } from "sonner";

import api from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";

const REPORTS = [
  ["ledger", "Ledger"],
  ["day-book", "Day Book"],
  ["trial-balance", "Trial Balance"],
  ["income-expense", "Income & Expense"],
  ["outstanding-dues", "Outstanding Dues"],
];

const inr = (amount) =>
  `₹${Number(amount || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

export default function FreshFinanceReports({ accounts = [] }) {
  const [report, setReport] = useState("ledger");
  const [accountId, setAccountId] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const params = useMemo(() => {
    const value = {};
    if (accountId) value.account_id = accountId;
    if (dateFrom) value.date_from = dateFrom;
    if (dateTo) value.date_to = dateTo;
    return value;
  }, [accountId, dateFrom, dateTo]);

  const load = async () => {
    setData(null);
    if (report === "ledger" && !accountId) {
      return;
    }
    setLoading(true);
    try {
      const response = await api.get(`/fresh-finance/reports/${report}`, { params });
      setData(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not load report");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [report]);

  const download = async (format) => {
    if (report === "ledger" && !accountId) {
      toast.error("Select an account first");
      return;
    }
    try {
      const response = await api.get(`/fresh-finance/reports/${report}/${format}`, {
        params,
        responseType: "blob",
      });
      const url = URL.createObjectURL(response.data);
      if (format === "pdf") {
        window.open(url, "_blank");
        return;
      }
      const link = document.createElement("a");
      link.href = url;
      link.download = `fresh-finance-${report}.xlsx`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not export report");
    }
  };

  return (
    <section data-testid="fresh-finance-reports-section">
      <div className="border-b border-gray-200 pb-3">
        <p className="text-xs font-semibold uppercase text-blue-700">Phase D</p>
        <h2 className="mt-1 font-display text-2xl font-bold">
          Ledger &amp; statutory reports
        </h2>
      </div>
      <div
        className="mt-4 flex flex-wrap border-b border-gray-200"
        data-testid="fresh-finance-report-tabs"
      >
        {REPORTS.map(([id, label]) => (
          <button
            className={`border-b-2 px-3 py-2 text-sm font-medium ${
              report === id
                ? "border-blue-700 text-blue-800"
                : "border-transparent text-gray-600 hover:text-gray-950"
            }`}
            data-testid={`fresh-finance-report-tab-${id}`}
            key={id}
            onClick={() => {
              setData(null);
              setReport(id);
            }}
            type="button"
          >
            {label}
          </button>
        ))}
      </div>
      <section className="mt-4 flex flex-wrap items-end gap-3 border border-gray-200 bg-white p-4">
        {report === "ledger" && (
          <Field label="Account">
            <select
              className="h-8 min-w-56 border border-gray-300 bg-white px-2 text-sm"
              data-testid="fresh-finance-report-ledger-account"
              onChange={(event) => setAccountId(event.target.value)}
              value={accountId}
            >
              <option value="">Select account</option>
              {accounts.map((account) => (
                <option key={account.account_id} value={account.account_id}>
                  {account.code} · {account.name}
                </option>
              ))}
            </select>
          </Field>
        )}
        {report !== "outstanding-dues" && (
          <>
            <Field label="From">
              <Input
                className="h-8 border-gray-300 text-sm"
                data-testid="fresh-finance-report-date-from"
                onChange={(event) => setDateFrom(event.target.value)}
                type="date"
                value={dateFrom}
              />
            </Field>
            <Field label="To">
              <Input
                className="h-8 border-gray-300 text-sm"
                data-testid="fresh-finance-report-date-to"
                onChange={(event) => setDateTo(event.target.value)}
                type="date"
                value={dateTo}
              />
            </Field>
          </>
        )}
        <Button
          data-testid="fresh-finance-report-run"
          disabled={loading || (report === "ledger" && !accountId)}
          onClick={load}
          size="sm"
          type="button"
          variant="outline"
        >
          <RefreshCw className="h-4 w-4" />
          Run report
        </Button>
        <div className="ml-auto flex gap-2">
          <Button
            data-testid="fresh-finance-report-excel"
            onClick={() => download("excel")}
            size="sm"
            type="button"
            variant="outline"
          >
            <Download className="h-4 w-4" />
            Excel
          </Button>
          <Button
            data-testid="fresh-finance-report-pdf"
            onClick={() => download("pdf")}
            size="sm"
            type="button"
          >
            <Printer className="h-4 w-4" />
            PDF
          </Button>
        </div>
      </section>
      <ReportResult data={data} report={report} loading={loading} />
    </section>
  );
}

function ReportResult({ data, loading, report }) {
  if (loading) {
    return (
      <p
        className="mt-4 text-sm text-gray-600"
        data-testid="fresh-finance-report-loading"
      >
        Loading report…
      </p>
    );
  }
  if (!data) {
    return (
      <p
        className="mt-4 text-sm text-gray-600"
        data-testid="fresh-finance-report-empty"
      >
        Choose filters and run a report.
      </p>
    );
  }
  if (report === "income-expense") {
    return <IncomeExpense data={data} />;
  }
  const columns = {
    ledger: ["Date", "Type", "Narration", "Debit", "Credit", "Balance"],
    "day-book": ["Date", "Type", "Narration", "Debit", "Credit"],
    "trial-balance": ["Code", "Account", "Type", "Debit", "Credit"],
    "outstanding-dues": ["Unit", "Bills", "Outstanding"],
  }[report];
  const values = (row) => {
    if (report === "ledger") {
      return [
        row.date,
        row.type,
        row.narration,
        inr(row.debit),
        inr(row.credit),
        `${row.balance_side} ${inr(row.running_balance)}`,
      ];
    }
    if (report === "day-book") {
      return [row.date, row.type, row.narration, inr(row.debit), inr(row.credit)];
    }
    if (report === "trial-balance") {
      return [row.code, row.account, row.type, inr(row.debit), inr(row.credit)];
    }
    return [row.unit_number, row.bills, inr(row.outstanding)];
  };
  return (
    <div
      className="mt-4 overflow-x-auto border border-gray-200"
      data-testid="fresh-finance-report-table"
    >
      <table className="w-full min-w-[720px] text-sm">
        <thead className="bg-gray-50 text-left text-xs font-semibold uppercase text-gray-600">
          <tr>
            {columns.map((column) => (
              <th className="px-3 py-2" key={column}>
                {column}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200 bg-white">
          {data.rows.length === 0 && (
            <tr>
              <td className="px-3 py-4 text-gray-500" colSpan={columns.length}>
                No entries found.
              </td>
            </tr>
          )}
          {data.rows.map((row, index) => (
            <tr key={`${index}-${row.journal_id || row.code || row.unit_number}`}>
              {values(row).map((value, valueIndex) => (
                <td
                  className={`px-3 py-2 ${
                    valueIndex >= columns.length - 2 ? "text-right tabular-nums" : ""
                  }`}
                  key={valueIndex}
                >
                  {value}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
        <tfoot className="border-t bg-gray-50 font-semibold">
          <SummaryRow columns={columns.length} summary={data.summary} />
        </tfoot>
      </table>
    </div>
  );
}

function IncomeExpense({ data }) {
  if (!Array.isArray(data?.income) || !Array.isArray(data?.expenses)) {
    return (
      <p
        className="mt-4 text-sm text-gray-600"
        data-testid="fresh-finance-income-expense-empty"
      >
        Income and expense data is loading.
      </p>
    );
  }
  const rows = [
    ...data.income.map((row) => ["Income", row.account, inr(row.amount)]),
    ...data.expenses.map((row) => ["Expense", row.account, inr(row.amount)]),
  ];
  return (
    <div
      className="mt-4 overflow-x-auto border border-gray-200"
      data-testid="fresh-finance-income-expense-table"
    >
      <table className="w-full min-w-[560px] text-sm">
        <thead className="bg-gray-50 text-left text-xs font-semibold uppercase text-gray-600">
          <tr>
            <th className="px-3 py-2">Category</th>
            <th className="px-3 py-2">Account</th>
            <th className="px-3 py-2 text-right">Amount</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200 bg-white">
          {rows.length === 0 && (
            <tr>
              <td className="px-3 py-4 text-gray-500" colSpan={3}>
                No income or expense entries found.
              </td>
            </tr>
          )}
          {rows.map(([category, account, amount]) => (
            <tr key={`${category}-${account}`}>
              <td className="px-3 py-2">{category}</td>
              <td className="px-3 py-2">{account}</td>
              <td className="px-3 py-2 text-right tabular-nums">{amount}</td>
            </tr>
          ))}
        </tbody>
        <tfoot className="border-t bg-gray-50 font-semibold">
          <tr>
            <td className="px-3 py-2" colSpan={2}>Surplus / (Deficit)</td>
            <td className="px-3 py-2 text-right tabular-nums">
              {inr(data.summary.surplus)}
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

function SummaryRow({ columns, summary }) {
  if (summary.outstanding !== undefined) {
    return (
      <tr>
        <td className="px-3 py-2" colSpan={columns - 1}>
          Total outstanding · {summary.units} units
        </td>
        <td className="px-3 py-2 text-right tabular-nums">{inr(summary.outstanding)}</td>
      </tr>
    );
  }
  if (summary.debit !== undefined) {
    return (
      <tr>
        <td className="px-3 py-2" colSpan={columns - 2}>
          Totals {summary.balanced === false ? "(unbalanced)" : ""}
        </td>
        <td className="px-3 py-2 text-right tabular-nums">{inr(summary.debit)}</td>
        <td className="px-3 py-2 text-right tabular-nums">{inr(summary.credit)}</td>
      </tr>
    );
  }
  if (summary.closing_balance !== undefined) {
    return (
      <tr>
        <td className="px-3 py-2" colSpan={columns - 1}>Closing balance</td>
        <td className="px-3 py-2 text-right tabular-nums">
          {summary.closing_side} {inr(summary.closing_balance)}
        </td>
      </tr>
    );
  }
  return null;
}

function Field({ children, label }) {
  return (
    <label className="grid gap-1 text-xs font-semibold text-gray-600">
      <span>{label}</span>
      {children}
    </label>
  );
}